import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from '../../api/axiosInstance';
import { getUserInfoDetails } from "../../utlis";
import { resetState, setBasicInfo, setCandidateId, setProfessionalInfo, setProfileInfo, setAuditInfo, setEmergencyContacts } from "../../features/candidate/personalSlice";
import { getEmergencyContact, searchCandidates } from "../../api/candidiate";
import { useCandidateForm } from "./CandidateFormContext";
import CandidateHeader from "./components/CandidateHeader";
import CandidateTable from "./components/CandidateTable";

const { companyId, clientId } = getUserInfoDetails();

function convertDateFormat(inputDate) {
  if (!inputDate || typeof inputDate !== 'string') {
    return inputDate;
  }

  const parts = inputDate.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${month}-${day}`;
  } else {
    return inputDate;
  }
}

const CandidateInner = () => {
  const [isLoading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [backendResults, setBackendResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { startCreateNewCandidate, resetCandidateData } = useCandidateForm();
  const referenceData = useSelector((state) => state.reference);
  const searchTimerRef = useRef(null);

  // Lookup technology_id from config by name or description (division text)
  const lookupTechnologyId = (division) => {
    if (!division) return '';
    const techs = referenceData.technologies?.technology || [];
    const match = techs.find(t => t.name === division || t.description === division);
    return match ? String(match.technology_id) : '';
  };

  // Filter state
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  const getCandidates = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/candidates', {
        params: { company: companyId, client: clientId, pageNum: 1, pageSize: 100 }
      });

      const responseData = res.data;

      // API returns HTTP 200 for all responses — check message and data presence
      if (!responseData?.data || !responseData.data.records) {
        const message = responseData?.message || 'Failed to load candidates';
        toast.error(message);
        setCandidates([]);
        return;
      }

      setCandidates(responseData.data.records || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 403) {
        toast.error('Access denied: You do not have permission to view candidates');
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again later');
      } else {
        toast.error(errorMessage || 'Failed to load candidates');
      }
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCandidates();
  }, []);

  // Backend search fallback — debounced
  const performBackendSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setBackendResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await searchCandidates(term);
      const records = response.data?.data?.records || [];
      setBackendResults(records);
    } catch (error) {
      setBackendResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change — local first, then backend fallback
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    clearTimeout(searchTimerRef.current);

    if (!value.trim()) {
      setBackendResults([]);
      return;
    }

    // Debounce backend search by 500ms
    searchTimerRef.current = setTimeout(() => {
      performBackendSearch(value.trim());
    }, 500);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter handlers
  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleCloseFilterPopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  // Filter search results for FilterPopup
  const getSearchResults = (searchTerm, selectedCategory) => {
    if (!searchTerm || !selectedCategory || !candidates) return [];

    return candidates
      .filter((candidate) => {
        let valueToSearch = '';

        switch (selectedCategory) {
          case 'Name':
            valueToSearch = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim();
            break;
          case 'Email':
            valueToSearch = candidate.email_id || '';
            break;
          case 'Phone':
            valueToSearch = candidate.mobile || '';
            break;
          case 'Technology':
            valueToSearch = candidate.division || '';
            break;
          case 'Job Title':
            valueToSearch = candidate.job_title || '';
            break;
          case 'Status':
            valueToSearch = candidate.status || '';
            break;
          default:
            valueToSearch = '';
        }

        return valueToSearch.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .map((candidate) => {
        switch (selectedCategory) {
          case 'Name':
            return `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim();
          case 'Email':
            return candidate.email_id;
          case 'Phone':
            return candidate.mobile;
          case 'Technology':
            return candidate.division;
          case 'Job Title':
            return candidate.job_title;
          case 'Status':
            return candidate.status;
          default:
            return null;
        }
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  const filterCategories = [
    'Name',
    'Email',
    'Phone',
    'Technology',
    'Job Title',
    'Status',
  ];

  // Handle add new candidate
  const handleAddCandidate = () => {
    dispatch(resetState());
    startCreateNewCandidate();
    navigate('add-candidate/personal/basic');
  };

  // Handle candidate click/edit
  const handleEditCandidate = async (candidate) => {
    if (candidate && candidate.candidate_id) {
      // Update Redux state with candidate data
      dispatch(setBasicInfo({
        firstName: candidate.first_name,
        lastName: candidate.last_name,
        middleName: candidate.middle_name,
        gender: candidate.gender ? candidate.gender.toLowerCase() : candidate.gender,
        maritalStatus: candidate.marital_status ? candidate.marital_status.toLowerCase() : candidate.marital_status,
        dob: convertDateFormat(candidate.date_of_birth || candidate.dob),
        email: candidate.email_id,
        address1: candidate.address1,
        address2: candidate.address2,
        phone: candidate.mobile,
        alternatePhoneNum: candidate.phone,
        city: candidate.city || '',
        state: candidate.state || '',
        zip: candidate.zip || '',
        status: candidate.status || '',
      }));
      dispatch(setProfessionalInfo({
        jobTitle: candidate.job_title,
        salesPerson: candidate.sales_person_id,
        technology: candidate.talent_id ? String(candidate.talent_id) : lookupTechnologyId(candidate.division),
        workPermitStatus: candidate.legal_status || '',
        workStatusIssueDate: convertDateFormat(candidate.work_status_issue_date),
        workStatusEndDate: convertDateFormat(candidate.work_status_enddate),
        SSN: candidate.ssn,
        employmentStatus: candidate.employment_status || '',
        referredBy: candidate.referred_by,
      }));
      dispatch(setProfileInfo({
        totalInterviews: candidate.total_interviews,
        activeInterviews: candidate.total_active_interviews,
        passedInterviews: candidate.total_passed_interviews,
        failedInterviews: candidate.total_failed_interviews,
        holdInterviews: candidate.total_onhold_interviews,
        lastInterviewOn: convertDateFormat(candidate.last_interview_on),
        rating: candidate.rating
      }));
      dispatch(setAuditInfo({
        createdBy: candidate.created_by,
        dateCreated: candidate.created,
        updatedBy: candidate.updated_by,
        lastUpdated: candidate.updated,
      }));
      dispatch(setCandidateId(candidate.candidate_id));

      // Fetch emergency contacts for this candidate
      try {
        const ecRes = await getEmergencyContact(candidate.candidate_id);
        // Response format per API spec: { message: "success", data: { contact_id1: {...}, contact_id2: {...} } }
        const ecData = ecRes.data?.data || {};

        // Contact may be an object (per API spec) or array (legacy) - handle both
        const getContact = (c) => {
          if (!c) return {};
          if (Array.isArray(c)) return c[0] || {};
          return c;
        };

        const contact1Data = getContact(ecData.contact_id1);
        const contact2Data = getContact(ecData.contact_id2);

        // Phone is a nested object { phone: "...", mobile: "...", Fax: "..." }
        const extractPhone = (phoneField) => {
          if (!phoneField) return '';
          if (typeof phoneField === 'object') return phoneField.phone || '';
          return String(phoneField);
        };

        const mapContact = (c) => ({
          firstName: c.first_name || '',
          lastName: c.last_name || '',
          contactPhoneNum: extractPhone(c.phone),
          email: c.email || '',
          contactRelation: c.relation || '',
          createdBy: c.created_by || '',
          dateCreated: c.created || '',
          updatedBy: c.updated_by || '',
          lastUpdated: c.updated || '',
        });

        dispatch(setEmergencyContacts({
          contact1: mapContact(contact1Data),
          contact2: mapContact(contact2Data),
        }));
      } catch (ecErr) {
        console.error('Error fetching emergency contacts:', ecErr);
        // Don't block navigation - emergency contacts may not exist yet
      }

      // Reset candidate form context for edit mode
      resetCandidateData();

      navigate('add-candidate/personal/basic');
    } else {
      toast.error('Candidate ID not found');
    }
  };

  // Filter candidates based on search term and applied filters
  const localFiltered = candidates.filter((candidate) => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${candidate.first_name || ''} ${candidate.middle_name || ''} ${candidate.last_name || ''}`.trim();

    // Apply search filter
    const matchesSearch = !searchTerm || (
      fullName.toLowerCase().includes(searchLower) ||
      candidate.email_id?.toLowerCase().includes(searchLower) ||
      candidate.mobile?.toLowerCase().includes(searchLower) ||
      candidate.division?.toLowerCase().includes(searchLower) ||
      candidate.job_title?.toLowerCase().includes(searchLower) ||
      candidate.status?.toLowerCase().includes(searchLower)
    );

    // Apply category filters
    const matchesFilters = !appliedFilters.length || appliedFilters.some(({ category, items }) => {
      if (!items || items.length === 0) return false;

      switch (category) {
        case 'Name':
          return items.some((name) => name.toLowerCase() === fullName.toLowerCase());
        case 'Email':
          return items.some((email) => email.toLowerCase() === (candidate.email_id || '').toLowerCase());
        case 'Phone':
          return items.some((phone) => phone === candidate.mobile);
        case 'Technology':
          return items.some((tech) => tech.toLowerCase() === (candidate.division || '').toLowerCase());
        case 'Job Title':
          return items.some((title) => title.toLowerCase() === (candidate.job_title || '').toLowerCase());
        case 'Status':
          return items.some((status) => status.toLowerCase() === (candidate.status || '').toLowerCase());
        default:
          return false;
      }
    });

    return matchesSearch && matchesFilters;
  });

  // Use local results if available, otherwise fall back to backend search results
  const filteredCandidates = (() => {
    if (localFiltered.length > 0 || !searchTerm.trim()) return localFiltered;
    if (backendResults.length === 0) return localFiltered;
    const localIds = new Set(localFiltered.map((c) => c.candidate_id));
    return [...localFiltered, ...backendResults.filter((c) => !localIds.has(c.candidate_id))];
  })();

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <CandidateHeader
          onReload={getCandidates}
          onAddCandidate={handleAddCandidate}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Backend search indicator */}
        {searchTerm.trim() && localFiltered.length === 0 && backendResults.length > 0 && (
          <div className="px-4 py-2 bg-blue-50 text-blue-700 text-sm border-b border-blue-100">
            Showing results from server search for "{searchTerm}"
          </div>
        )}
        {searchTerm.trim() && isSearching && localFiltered.length === 0 && (
          <div className="px-4 py-2 bg-gray-50 text-gray-500 text-sm border-b border-gray-100">
            Searching server...
          </div>
        )}

        {/* Table with loading overlay */}
        <div className="relative">
          <CandidateTable
            data={filteredCandidates}
            isLoading={isLoading}
            onEditCandidate={handleEditCandidate}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            isFilterPopupOpen={isFilterPopupOpen}
            onFilterClick={handleFilterClick}
            onCloseFilterPopup={handleCloseFilterPopup}
            onApplyFilters={handleApplyFilters}
            getSearchResults={getSearchResults}
            filterCategories={filterCategories}
            tableData={candidates}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateInner;
